/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable consistent-return */

import {
  Box,
  Button,
  Stack,
  Text,
  HStack,
  Divider,
  Input,
  FormControl,
  FormErrorMessage,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  ModalFooter,
  InputGroup,
  InputRightElement,
  useTheme as useChakraTheme,
  Icon,
  Flex,
} from '@chakra-ui/core';
import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';

import {
  MdClose,
  MdOutlineBorderColor,
  MdOutlineCheckBoxOutlineBlank,
  MdOutlineRemoveRedEye,
  MdOutlineUploadFile,
} from 'react-icons/md';

import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { useCurrentUserProfile } from '../../../../../app/hooks/useCurrentUserProfile';

import useHireFormErrorHandling, { ValidationType, ValidationTypeEnum } from '../../hooks/useHireFormErrorHandling';
import { useGetItemProperty } from '../../hooks/useGetItemProperty';
import { useUpdateFormItem } from '../../hooks/useUpdateFormItem';
import FormLabelInput from '../../formInputs/FormLabelInput';
import FormTextInput from '../../formInputs/FormTextInput';
import { FieldDocumentReviewT, FormBuilderComponentProps } from '../../types';
import DocumentDescriptionTextInput from './DocumentDescriptionTextInput';
import DocumentAcknowledgeTextInput from './DocumentAcknowledgeTextInput';

const DocumentReviewFieldComponent = ({ fieldId, isAdvanced, labelRightRenderer }: Omit<FormBuilderComponentProps, 'path'>) => {
  return (
    <Stack spacing={4}>
      <HStack justify="space-between">
        <FormLabelInput fieldId={fieldId} isAdvanced={isAdvanced} />
        {labelRightRenderer}
      </HStack>
      <FormTextInput fieldId={fieldId} placeholder="Enter text here..." isAdvanced={isAdvanced || false} />
      <DocumentDescriptionTextInput fieldId={fieldId} isAdvanced={isAdvanced || false} />
      <DocumentUpload fieldId={fieldId} />
      <HStack alignItems="start" spacing={2}>
        <Box pt="0.4rem">
          <Icon as={MdOutlineCheckBoxOutlineBlank} boxSize={5} color="gray.500" />
        </Box>
        <DocumentAcknowledgeTextInput fieldId={fieldId} isAdvanced={isAdvanced || false} />
      </HStack>
      <HStack>
        <DocumentPreviewButton fieldId={fieldId} />
      </HStack>
    </Stack>
  );
};

const DocumentUpload: FC<Omit<FormBuilderComponentProps, 'labelRightRenderer' | 'path'>> = React.memo(
  function DocumentUploadComponent({ fieldId, isAdvanced }) {
    const { t } = useTranslation('hrFormTemplate');
    const language = useStoreState((state) => state.formBuilder.currentLanguage);
    const removeError = useStoreActions((state) => state.formBuilder.removeError);
    const currentFieldValue = useGetItemProperty<FieldDocumentReviewT['url'] | null | undefined>(fieldId, 'documentReview.url'); // Uses renamed prop
    const handleOnChange = useUpdateFormItem();
    const inputId = useMemo(() => `document-upload_${fieldId}`, [fieldId]);
    const [file, setFile] = useState<File | null>(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);

    useEffect(() => {
      if (file) {
        const objectUrl = URL.createObjectURL(file);
        setFilePreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      }
      setFilePreviewUrl(null);
    }, [file]);

    const storedPdfUrl = useMemo(() => currentFieldValue?.[language] ?? null, [currentFieldValue, language]);

    function extractCleanFileNameFromUrl(urlString: string): string | null {
      try {
        const url = new URL(urlString);
        const pathname = decodeURIComponent(url.pathname);

        const lastSlashIndex = pathname.lastIndexOf('/');
        const fileNameWithPrefix = lastSlashIndex !== -1 ? pathname.substring(lastSlashIndex + 1) : pathname;

        if (!fileNameWithPrefix) {
          return null;
        }

        const serverCodeRegex = /^\d+-\d+_\s*/;

        const cleanFileName = fileNameWithPrefix.replace(serverCodeRegex, '');

        return cleanFileName;
      } catch (error) {
        return null;
      }
    }

    const displayUrl = filePreviewUrl || storedPdfUrl;
    const displayName = file?.name || extractCleanFileNameFromUrl(storedPdfUrl!) || '';
    const innerPath = React.useMemo(() => `url.${language}`, [language]);
    const { validate, error: validationError, hasError, inputRef } = useHireFormErrorHandling({
      targetId: fieldId,
      innerPath,
      validationType: ValidationTypeEnum.DOCUMENT_PREVIEW_FILE as ValidationType,
      isAdvanced: isAdvanced || false,
    });

    const handleFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
          const fileValidationData = { mimetype: selectedFile.type, size: selectedFile.size };
          const isValid = validate({ file: fileValidationData });
          if (isValid) {
            setFile(selectedFile);
            removeError({ targetId: fieldId, innerPath });
          } else {
            setFile(null);
            if (inputRef.current) inputRef.current.value = '';
          }
        }
      },
      [validate, inputRef, innerPath],
    );

    const handleUploadedUrl = useCallback(
      (uploadedUrl: string) => {
        const currentUrlObj = currentFieldValue?.url || {};

        handleOnChange(fieldId, {
          documentReview: {
            url: { ...currentUrlObj, [language]: uploadedUrl },
          },
        });
        if (inputRef.current) inputRef.current.value = '';
        setFile(null);
      },
      [currentFieldValue, language, handleOnChange, inputRef],
    );

    const handleCancelUpload = useCallback(() => {
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    }, [inputRef]);

    return (
      <FormControl isInvalid={hasError}>
        <Box
          bg={hasError ? 'red.50' : '#F0F8FE'}
          borderRadius="md"
          p={6}
          border="2px dashed"
          borderColor={hasError ? 'red.300' : 'gray.300'}
        >
          <Input ref={inputRef} accept="application/pdf" type="file" display="none" onChange={handleFileChange} id={inputId} />
          <Stack align="center" spacing={3}>
            <Box bg="blue.100" p={4} borderRadius="full">
              <Icon as={MdOutlineUploadFile} boxSize={6} color="blue.500" />
            </Box>
            <Box color="gray.500" fontSize="sm" fontWeight="normal" textAlign="center" lineHeight="tall">
              <Text>{t('formBuilder.documentReview.description1', { lng: language })}</Text>
              {t('formBuilder.documentReview.description2', { lng: language })}
            </Box>
            {(file || displayName) && (
              <HStack spacing={2} align="center" bg={file ? 'blue.50' : 'gray.100'} borderRadius="xl" p={2} px={3} maxW="100%">
                <Text color="gray.700" isTruncated fontSize="sm" maxW="250px">
                  {displayName}
                </Text>
                <HStack divider={<Divider orientation="vertical" h="16px" />} spacing={1}>
                  <DocumentPdfViewer
                    url={displayUrl ?? ''}
                    file={file}
                    onUpload={handleUploadedUrl}
                    onCancel={handleCancelUpload}
                  />
                  {file && (
                    <Button
                      variant="ghost"
                      size="xs"
                      colorScheme="gray"
                      onClick={handleCancelUpload}
                      aria-label="Clear selected file"
                    >
                      <Icon as={MdClose} color="gray.500" />
                    </Button>
                  )}
                </HStack>
              </HStack>
            )}
            {!file && (
              <Button as="label" htmlFor={inputId} colorScheme="blue" size="sm" cursor="pointer">
                {t('formBuilder.documentReview.chooseFiles', { lng: language })}
              </Button>
            )}
          </Stack>
        </Box>
        {hasError && validationError && <FormErrorMessage fontSize="xs">{t(validationError)}</FormErrorMessage>}
      </FormControl>
    );
  },
);

interface DocumentPdfViewerProps {
  url: string;
  file: File | null;
  onUpload: (uploadedUrl: string) => void;
  onCancel: () => void;
}

const DocumentPdfViewer: FC<DocumentPdfViewerProps> = React.memo(
  function DocumentPdfViewerComponent({ url, file, onUpload, onCancel }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { uploadDocument } = useStoreActions((state) => state.formBuilder);
    const { uploadingDocument } = useStoreState((state) => state.formBuilder);
    const { currentUserProfile } = useCurrentUserProfile();
    const accountId = currentUserProfile?.account ?? '';

    useEffect(() => {
      if (file && url.startsWith('blob:')) {
        onOpen();
      }
    }, [file, url, onOpen]);

    const handleUpload = useCallback(async () => {
      if (!file) return;
      const payload = new FormData();
      payload.append('file', file);
      try {
        const responseUrl = await uploadDocument({ account: accountId, payload });
        onUpload(responseUrl);
      } finally {
        onClose();
      }
    }, [file, uploadDocument, accountId, onUpload, onClose]);

    const handleModalClose = useCallback(() => {
      if (!uploadingDocument) {
        onCancel();
        onClose();
      }
    }, [uploadingDocument, onCancel, onClose]);

    return (
      <>
        <Button variant="ghost" size="xs" colorScheme="gray" onClick={onOpen} title="Preview" aria-label="Preview document">
          <Icon as={MdOutlineRemoveRedEye} />
        </Button>
        <Modal
          blockScrollOnMount={false}
          isOpen={isOpen}
          onClose={handleModalClose}
          size="6xl"
          isCentered
          closeOnOverlayClick={!uploadingDocument}
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent borderRadius="md" h="98vh" maxH="98vh">
            <ModalBody p={0}>
              {url ? (
                <embed
                  type="application/pdf"
                  src={url}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="PDF Preview"
                />
              ) : (
                <Flex justify="center" align="center" h="100%">
                  <Text>No document to preview.</Text>
                </Flex>
              )}
            </ModalBody>
            <ModalFooter py={1}>
              <Button colorScheme="gray" variant="ghost" mr={3} onClick={handleModalClose} isDisabled={uploadingDocument}>
                Close
              </Button>
              {file && (
                <Button colorScheme="blue" isLoading={uploadingDocument} onClick={handleUpload}>
                  Save
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  },
  (p, n) =>
    /* eslint-disable-next-line */
    p.url === n.url && p.file === n.file && p.onUpload === n.onUpload && p.onCancel === n.onCancel,
);

const DocumentPreviewButton: FC<Pick<FormBuilderComponentProps, 'fieldId' | 'isAdvanced'>> = React.memo(
  function DocumentPreviewButtonComponent({ fieldId, isAdvanced }) {
    const language = useStoreState((state) => state.formBuilder.currentLanguage);

    const [inputValue, setInputValue] = useState('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const { t } = useTranslation('administration');
    const { t: tForm } = useTranslation('hrFormTemplate');
    const chakraTheme = useChakraTheme();

    const currentFieldValue = useGetItemProperty<FieldDocumentReviewT['confirmText'] | null | undefined>(
      fieldId,
      'documentReview.confirmText',
    );
    const handleOnChange = useUpdateFormItem();

    const { validate, error: validationError, hasError, inputRef } = useHireFormErrorHandling({
      targetId: fieldId,
      innerPath: `confirmText.${language}`,
      validationType: ValidationTypeEnum.DOCUMENT_PREVIEW_CONFIRM_TEXT as ValidationType,
      isAdvanced: isAdvanced || false,
    });

    useEffect(() => {
      setInputValue(currentFieldValue?.[language] ?? '');
    }, [currentFieldValue, language]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        validate(newValue);
      },
      [validate],
    );

    const commitChanges = useCallback(() => {
      const currentConfirmTextObj = currentFieldValue?.confirmText || {};
      if (inputValue !== (currentFieldValue?.[language] ?? '')) {
        handleOnChange(fieldId, {
          documentReview: {
            confirmText: { ...currentConfirmTextObj, [language]: inputValue },
          },
        });
      }
    }, [inputValue, currentFieldValue, language, handleOnChange]);

    const handleInputBlur = useCallback(() => {
      validate(inputValue);
      commitChanges();
      setIsEditing(false);
    }, [validate, inputValue, commitChanges]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          validate(inputValue);
          commitChanges();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setInputValue(currentFieldValue?.[language] ?? '');
        }
      },
      [validate, inputValue, commitChanges, currentFieldValue, language],
    );

    return (
      <FormControl isInvalid={hasError}>
        <Box
          as="div"
          outline={isEditing ? `2px solid ${chakraTheme.colors.blue[500]}` : `1px solid ${chakraTheme.colors.blue[600]}`}
          borderRadius="full"
          bg={isEditing ? chakraTheme.colors.blue[50] : 'transparent'}
          transition="background-color 0.2s, outline-color 0.2s"
          onClick={() => {
            if (!isEditing) inputRef.current?.focus();
          }}
          width="fit-content"
          cursor="text"
        >
          <InputGroup size="sm">
            <Input
              ref={inputRef}
              variant="unstyled"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => setIsEditing(true)}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              textAlign="center"
              px={4}
              py={1.5}
              color={chakraTheme.colors.blue[600]}
              fontWeight="bold"
              fontSize="sm"
              placeholder={t('companyResources.consentButtonPlaceholder') || 'Button Text'}
              _placeholder={{ color: chakraTheme.colors.blue[300], fontWeight: 'normal' }}
              _focus={{
                textAlign: 'left',
              }}
            />
            {!isEditing && (
              <InputRightElement pointerEvents="none" h="100%">
                <Icon as={MdOutlineBorderColor} color={chakraTheme.colors.blue[400]} />
              </InputRightElement>
            )}
          </InputGroup>
        </Box>
        {hasError && validationError && <FormErrorMessage fontSize="xs">{tForm(validationError)}</FormErrorMessage>}
      </FormControl>
    );
  },
  (p, n) => /* eslint-disable-next-line */ p.fieldId === n.fieldId,
);

export const DocumentReviewField = React.memo(
  DocumentReviewFieldComponent,
  (prev, next) =>
    // eslint-disable-next-line
    prev.fieldId === next.fieldId && prev.isAdvanced === next.isAdvanced && prev.labelRightRenderer === next.labelRightRenderer,
);
